/**
 * SP5 Wave 3 — CISO `/benchmarks/:id` Related-Benchmarks section.
 *
 * Rendered at the bottom of the benchmark detail page. The section heading
 * is always visible (layout stability) — the body switches between
 * loading / error / empty / list states.
 */

import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { useRelatedBenchmarks } from '@/hooks/useRelatedBenchmarks'
import type { RelatedBenchmark } from '@intelhub/shared'

interface Props {
  benchmarkId: string
  limit?: number
}

export function RelatedBenchmarks({ benchmarkId, limit = 5 }: Props) {
  const { data, isLoading, isError } = useRelatedBenchmarks(benchmarkId, limit)

  return (
    <section
      className="mt-10 border-t pt-6"
      aria-labelledby="related-benchmarks-heading"
    >
      <h2
        id="related-benchmarks-heading"
        className="mb-4 text-lg font-semibold"
      >
        Related Benchmarks
      </h2>
      {isLoading ? (
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          aria-busy="true"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              data-testid="related-skeleton"
              className="h-28 animate-pulse rounded-md bg-muted"
            />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-muted-foreground">
          Unable to load related reports right now.
        </p>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          More reports coming — we're building out this topic.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((r) => (
            <RelatedCard key={r.id} r={r} />
          ))}
        </div>
      )}
    </section>
  )
}

function RelatedCard({ r }: { r: RelatedBenchmark }) {
  const publisherLabel = r.publisherDisplayName ?? r.publisherSlug ?? ''
  return (
    <Link to={`/benchmarks/${r.id}`} className="block">
      <Card className="h-full transition-colors hover:bg-muted/50">
        <CardContent className="flex flex-col gap-2 p-4">
          <span className="line-clamp-2 font-medium">{r.title}</span>
          <span className="text-xs text-muted-foreground">
            {publisherLabel}
            {r.reportType ? ` · ${r.reportType}` : ''}
          </span>
          {r.topics.length > 0 && (
            <span className="line-clamp-1 text-xs text-muted-foreground">
              {r.topics.slice(0, 3).join(' · ')}
            </span>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}
